import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { getErrorMessage } from '../../utils/errorHandler';
import notificationsAPI, { INotification, ReplyNotificationParams } from './notificacionesAPI';
import { APIResponse } from '../../services/api';

interface NotificationState {
  status: 'loading' | 'idle' | 'error';
  error?: string;
  list?: INotification[];
  number?: number;
}

export const initialState: NotificationState = {
  status: 'idle',
};

export const getNotifications = createAsyncThunk<
  INotification[],
  void,
  { rejectValue: string }
>('notificaciones/', async (_, thunkAPI) => {
  try {
    const response = await notificationsAPI.getNotifications();

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    console.error(err);
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const getUnreadNotNumber = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>('notificaciones/status', async (_, thunkAPI) => {
  try {
    const response = await notificationsAPI.getUnreadNotNumber();

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const setReadNotification = createAsyncThunk<
  INotification,
  INotification,
  { rejectValue: string }
>('notificaciones/read', async (notification, thunkAPI) => {
  try {
    let response: APIResponse<INotification>;

    if (notification.tipo === 'web') {
      response = await notificationsAPI.setReadNotification(notification.id);
    } else {
      response = await notificationsAPI.setReadNotification(notification.tipo_comprob + '-' + notification.nro_comprob);
    }

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const replyNotification = createAsyncThunk<
  INotification,
  ReplyNotificationParams,
  { rejectValue: string }
>('notificaciones/reply', async (params, thunkAPI) => {
  try {
    const response = await notificationsAPI.replyNotification(params);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const NotificacionesState = createSlice({
  name: 'NotificacionesState',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getNotifications.pending, state => {
        state.status = 'loading';
        state.list = undefined;
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });

    builder
      .addCase(getUnreadNotNumber.pending, state => {
        state.status = 'loading';
      })
      .addCase(getUnreadNotNumber.fulfilled, (state, action) => {
        state.status = 'idle';
        state.number = action.payload;
      })
      .addCase(getUnreadNotNumber.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });

    builder
      .addCase(setReadNotification.pending, state => {
        state.status = 'loading';
      })
      .addCase(setReadNotification.fulfilled, (state, action) => {
        if (!state.list) {
          return;
        }

        const newArray = state.list.map(notification => {
          if (notification.tipo === 'web' && action.payload.tipo === 'web') {
            if (notification.id === action.payload.id) {
              return action.payload;
            }
          } else if (notification.tipo === 'rafam' && action.payload.tipo === 'rafam') {
            if (notification.tipo_comprob === action.payload.tipo_comprob &&
                notification.nro_comprob === action.payload.nro_comprob
            ) {
              return action.payload;
            }
          }

          return notification;
        });

        if (state.number) {
          state.number -= 1;
        }
        state.list = newArray;
        state.status = 'idle';
      })
      .addCase(setReadNotification.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });

    builder
      .addCase(replyNotification.pending, state => {
        state.status = 'loading';
      })
      .addCase(replyNotification.fulfilled, (state, action) => {
        if (!state.list) {
          return;
        }

        const newArray = state.list.map(notification => {
          if (notification.tipo === 'web' && action.payload.tipo === 'web') {
            if (notification.id === action.payload.id) {
              return action.payload;
            }
          } else if (notification.tipo === 'rafam' && action.payload.tipo === 'rafam') {
            if (notification.tipo_comprob === action.payload.tipo_comprob &&
                notification.nro_comprob === action.payload.nro_comprob
            ) {
              return action.payload;
            }
          }

          return notification;
        });

        if (state.number) {
          state.number -= 1;
        }
        state.list = newArray;
        state.status = 'idle';
      })
      .addCase(replyNotification.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
  },
});

export const getNotificacionesList = (state: RootState) => state.notificaciones.list ?? [];
export const getNotificacionesStatus = (state: RootState) => state.notificaciones.status ?? [];

export const getNotificacionesNumber = (state: RootState) => state.notificaciones.number ?? 0;

export default NotificacionesState.reducer;
